export interface CachedSigningToken {
  username: string;
  token: string;
  expiresAt: string | null;
  checkInId: number;
}

const DB_NAME = 'h3ight-shift-signing-tokens';
const DB_VERSION = 1;
const STORE_NAME = 'tokens';

function normalizeUsername(username: string): string {
  return username.trim().toLowerCase();
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'username' });
        store.createIndex('checkInId', 'checkInId', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open signing token cache'));
    request.onblocked = () => reject(new Error('Signing token cache is blocked'));
  });
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function transactionToPromise(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'));
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
  });
}

function closeDatabase(db: IDBDatabase) {
  db.close();
}

export function isTokenExpired(entry: CachedSigningToken): boolean {
  if (!entry.expiresAt) {
    // The server always sets an expiry; null means unknown, so treat it as expired.
    return true;
  }
  return Date.now() >= new Date(entry.expiresAt).getTime();
}

export async function cacheSigningToken(entry: CachedSigningToken): Promise<void> {
  const db = await openDatabase();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const normalized = {
      ...entry,
      username: normalizeUsername(entry.username),
    };
    await Promise.all([
      requestToPromise(store.put(normalized)),
      transactionToPromise(tx),
    ]);
  } finally {
    closeDatabase(db);
  }
}

export async function getCachedSigningToken(username: string): Promise<CachedSigningToken | null> {
  const db = await openDatabase();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const [record] = await Promise.all([
      requestToPromise<CachedSigningToken | undefined>(store.get(normalizeUsername(username))),
      transactionToPromise(tx),
    ]);

    if (!record) {
      return null;
    }

    if (isTokenExpired(record)) {
      const deleteTx = db.transaction(STORE_NAME, 'readwrite');
      const deleteStore = deleteTx.objectStore(STORE_NAME);
      await Promise.all([
        requestToPromise(deleteStore.delete(record.username)),
        transactionToPromise(deleteTx),
      ]);
      return null;
    }

    return record;
  } finally {
    closeDatabase(db);
  }
}

export async function removeCachedSigningToken(username: string): Promise<void> {
  const db = await openDatabase();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    await Promise.all([
      requestToPromise(store.delete(normalizeUsername(username))),
      transactionToPromise(tx),
    ]);
  } finally {
    closeDatabase(db);
  }
}

export async function removeCachedSigningTokensForCheckIn(checkInId: number): Promise<void> {
  const db = await openDatabase();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('checkInId');
    const request = index.openCursor(IDBKeyRange.only(checkInId));

    await new Promise<void>((resolve, reject) => {
      const cursorRequest = request;
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      cursorRequest.onerror = () => reject(cursorRequest.error ?? new Error('IndexedDB cursor failed'));
    });

    await transactionToPromise(tx);
  } finally {
    closeDatabase(db);
  }
}

export async function clearAllCachedSigningTokens(): Promise<void> {
  const db = await openDatabase();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    await Promise.all([
      requestToPromise(store.clear()),
      transactionToPromise(tx),
    ]);
  } finally {
    closeDatabase(db);
  }
}

export const __clearAllCachedSigningTokens = clearAllCachedSigningTokens;
